import { Preferences } from '@capacitor/preferences';
import { ToastController } from '@ionic/angular';
import { showToast } from '../Utilities';
import { syncGameStateWithServer } from 'src/app/utils/game-state-manager';

const toastController = new ToastController();

export type Enhancement = 'speed' | 'clairvoyance' | 'invincibility';

export interface Perishable {
  lifespan: number;// (s)
  enhancement: Enhancement;
  name: string;
}

export const perishables = new Map<string, Perishable>([
    ['blackberries', {name: 'blackberries', lifespan: 10, enhancement: 'speed'}],
    ['persimmon', {name: 'persimmon', lifespan: 15, enhancement: 'speed'}],
    ['pawpaw', {name: 'pawpaw', lifespan: 20, enhancement: 'speed'}],
    ['shroom', {name: 'shroom', lifespan: 30, enhancement: 'clairvoyance'}],
    ['cookie', {name: 'cookie', lifespan: 60, enhancement: 'invincibility'}]
  ]
)

export interface ItemInterface{
  name: string,
  texture?: string,
  animationKey?: string,
  quantity?: number,
  unlocks?: string[],
  lastModifiedAt?: string, // ISO String
}

type ItemMap = {
  [key: number]: ItemInterface;
};

export class Inventory {
  static maxColumns: integer = 7;
  static maxRows: integer = 2;
  static observers: Array<any> = [];
  static selected: integer = 0;
  static items: ItemMap = {};
  static activeEnhancements: Array<Enhancement> = [];

  static async loadInventory() {
    const ret = await Preferences.get({ key: 'inventory' });
    if (ret.value) {
      this.items = JSON.parse(ret.value);
      console.debug('Inventory Loaded:');
      console.debug(this.items);

      Object.values(this.items).forEach((item: ItemInterface) => {
        this.startExpiryTimer(item);
      });

      this.broadcast();
    } else {
      console.debug('No existing inventory found');
    }
  }

  static subscribe(fn: any) {
    this.observers.push(fn);
  }
  
  static unsubscribe(fn) {
    this.observers = this.observers.filter((subscriber) => subscriber !== fn);
  }

  static async broadcast() {
    this.observers.forEach((subscriber) => subscriber());
    await Preferences.set({
      key: 'inventory',
      value: JSON.stringify(this.items),
    });
  }

  static async timedRemoval(perishable: Perishable){
    setTimeout(async () => {
      this.removeItem(perishable.name);
      console.log(`1 ${perishable.name} depleted`);
      if(this.getItemQuantity(perishable.name) > 0){
        this.timedRemoval(perishable);
      }
      else{
        this.activeEnhancements = this.activeEnhancements.filter((e) => {return e !== perishable.enhancement});
        const capitalizedEnhancement = perishable.enhancement.charAt(0).toUpperCase()
        + perishable.enhancement.slice(1)
        
        // display toast indication that the enhancement has worn off
        const toast = await toastController.create({
          message: `${capitalizedEnhancement} has worn off!`,
          duration: 1500,
          color: 'warning',
          position: 'top',
        });
        await toast.present();
      }
    }, 1000*perishable.lifespan);
  }

  static async startExpiryTimer(item: ItemInterface){
    // set expiry timer to consume resource and enable enhancement
    if(perishables.has(item.name)){
      const perishable = perishables.get(item.name);
      if(!this.activeEnhancements.includes(perishable.enhancement)){

        // enable the consumable resources ability
        this.activeEnhancements.push(perishable.enhancement);

        // set an re-occurring expiation timer on the item
        this.timedRemoval(perishable);

        const capitalizedEnhancement = perishable.enhancement.charAt(0).toUpperCase()
        + perishable.enhancement.slice(1)

        // display toast indicating that the enhancement has been activated.
        const toast = await toastController.create({
          message: `${capitalizedEnhancement} activated!`,
          duration: 1500,
          color: 'success',
          position: 'top',
        });
        await toast.present();

      }
    }
  }

  static async addItem(item: ItemInterface) {
    let existingKey = Object.keys(this.items).find(
      (key) => this.items[key].name === item.name
    );
    if (existingKey) {
      // if item is a key, check if there there is already a key that opens the same doors
      if(item.name === "key" && item.unlocks){
        showToast({ message: `You found the ${item.unlocks.join(", ")} key!`});
        for (const [index, value] of Object.entries(this.items)) {
          if(value.name === "key"){
            let currentAccess = value.unlocks;
            item.unlocks = currentAccess.concat(item.unlocks);
            item.quantity = value.quantity + 1;
            item.lastModifiedAt = new Date().toISOString();
            this.items[index] = item;
          }
        }
      }

      // otherwise if it is a heart or regular drop item (no animation)
      else if((item.name === 'heart' || !item.animationKey)){
        this.items[existingKey].quantity += 1;
        this.items[existingKey].lastModifiedAt = new Date().toISOString();
      }
    } else {
      for (let index = 0; index < this.maxColumns * this.maxRows; index++) {
        let existingItem = this.items[index];
        if (!existingItem) {
          item.quantity = 1;
          item.lastModifiedAt = new Date().toISOString();
          // set expiry timer to consume resource and enable enhancement
          this.startExpiryTimer(item);
          this.items[index] = item;
          break;
        }
      }
    }
    await this.broadcast();

    // Update clientModifiedAt in Preferences
    Preferences.set({
      key: 'clientModifiedAt',
      value: new Date().toISOString(),
    });
    syncGameStateWithServer();
  }

  static removeItem(itemName: string, count = 1) {
    while(count > 0){
      let existingKey = Object.keys(this.items).find(
        (key) => this.items[key].name === itemName
      );
      if (existingKey && !this.items[existingKey].animationKey) {
        this.items[existingKey].quantity--;
        this.items[existingKey].lastModifiedAt = new Date().toISOString();
        if (this.items[existingKey].quantity <= 0){
          delete this.items[existingKey];
          break;
        }
      }
      count --;
    }
    this.broadcast();
    // Update clientModifiedAt in Preferences
    Preferences.set({
      key: 'clientModifiedAt',
      value: new Date().toISOString(),
    });
    syncGameStateWithServer();
  }

  static getItem(index) {
    return this.items[index];
  }

  static getItems() {
    return Object.values(this.items);
  }

  static getKeys(): ItemInterface {
    return Object.values(this.items).filter((i) => i.name === 'key')[0];
  }

  static containsAllPassages(passages: string[]){
    let key = this.getKeys();

    // first verify the key was found, and that it unlocks some passages
    if(!key || !key.unlocks || key.unlocks.length === 0){
      return false;
    }
    else{
      // if a key exists, then check if all the passages provided
      // have already been included
      for(let i=0; i< passages.length; i++){
        if(!key.unlocks.includes(passages[i])){
          return false;
        }
      }
      return true;
    }
  }

  static moveItem(start, end) {
    if (start === end || this.items[end]) return;
    this.items[end] = this.items[start];
    delete this.items[start];
    this.broadcast();
  }

  static get selectedItem() {
    return this.items[this.selected];
  }

  static getItemFrame(item) {
    return item.name;
  }

  static getItemQuantity(itemName: string): number {
    return Object.values(this.items)
      .filter((i) => i.name === itemName)
      .map((i) => i.quantity)
      .reduce((accumulater, currentValue) => accumulater + currentValue, 0);
  }

  static randomDrop(): ItemInterface | null {
    let item: ItemInterface = null;
    // Filter keys based on criteria
    let existingKeys: string[] = Object.keys(this.items).filter((key) => {
      const item = this.items[Number(key)];
      return (!item.animationKey && !item.unlocks && item.name !== "skull") || item.name === 'heart';
    });
    console.log("filtered items:");
    console.log(existingKeys.map(key => this.items[Number(key)]));
    if (existingKeys.length > 0 ) {
      const existingKey = existingKeys[Math.floor(Math.random() * existingKeys.length)];
      item = this.items[existingKey] as ItemInterface;
      console.log(existingKey);
      console.log(item);
      item.quantity--;
      this.items[existingKey] = item;
      if (item.quantity <= 0) delete this.items[existingKey];
    }
    this.broadcast();
    return item;
  }
}
